import { AnomalyIncident } from '../../../../../shared/models/anomaly-detection/models/AnomalyIncident';

export type GroupedAnomalyMarker = {
  dateKey: string;
  averagePosition?: AnomalyIncident;
  brandScore?: AnomalyIncident;
  visibility?: AnomalyIncident;
};

/** Weekly chart buckets use `prepareHeaderDate` keys (`YYYY-MM-DD - YYYY-MM-DD`); anomalies key weeks by Monday (`endDate`). */
const buildMondayToChartDateKey = (chartDateKeys: Set<string>): Map<string, string> => {
  const mondayToChartKey = new Map<string, string>();

  for (const chartDateKey of Array.from(chartDateKeys)) {
    const mondayKey = chartDateKey.includes(' - ')
      ? chartDateKey.split(' - ')[0].trim()
      : chartDateKey;
    mondayToChartKey.set(mondayKey, chartDateKey);
  }

  return mondayToChartKey;
};

export const groupAnomaliesByChartDate = (
  chartMatrix: (string | number)[][],
  averagePositionAnomalies: AnomalyIncident[],
  brandScoreAnomalies: AnomalyIncident[],
  visibilityAnomalies: AnomalyIncident[] = [],
): GroupedAnomalyMarker[] => {
  if (chartMatrix.length <= 1) {
    return [];
  }

  const chartDateKeys = new Set<string>();
  for (let rowIndex = 1; rowIndex < chartMatrix.length; rowIndex++) {
    const row = chartMatrix[rowIndex];
    if (!row?.[0]) {
      continue;
    }
    chartDateKeys.add(String(row[0]));
  }

  const mondayToChartDateKey = buildMondayToChartDateKey(chartDateKeys);
  const grouped = new Map<string, GroupedAnomalyMarker>();

  const addIncident = (
    anomaly: AnomalyIncident,
    field: 'averagePosition' | 'brandScore' | 'visibility',
  ): void => {
    const chartDateKey = mondayToChartDateKey.get(anomaly.endDate);
    if (!chartDateKey) {
      return;
    }
    const existing = grouped.get(chartDateKey) ?? { dateKey: chartDateKey };
    existing[field] = anomaly;
    grouped.set(chartDateKey, existing);
  };

  averagePositionAnomalies.forEach((anomaly) => addIncident(anomaly, 'averagePosition'));
  brandScoreAnomalies.forEach((anomaly) => addIncident(anomaly, 'brandScore'));
  visibilityAnomalies.forEach((anomaly) => addIncident(anomaly, 'visibility'));

  return Array.from(grouped.values()).sort((left, right) => left.dateKey.localeCompare(right.dateKey));
};
